import { useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  Save,
  Send,
  Eye,
  FileText,
  Copy,
  RotateCcw,
  AlertTriangle,
  CheckCircle2,
  Clock,
  DollarSign
} from "lucide-react";
import { BudgetFormData, BudgetValidationResult } from "@/types/budget";

interface BudgetFormActionsProps {
  isEditing: boolean;
  isSubmitting: boolean;
  onSave: () => void;
  onSaveAndSend?: () => void;
  onPreview?: () => void;
  onDuplicate?: () => void;
  onSaveAsTemplate?: () => void;
  onReset?: () => void;
  validation?: BudgetValidationResult;
  autoSaveStatus?: {
    isSaving: boolean;
    lastSaved?: Date;
    status: "idle" | "saving" | "saved" | "error";
  };
  className?: string;
}

export function BudgetFormActions({
  isEditing,
  isSubmitting,
  onSave,
  onSaveAndSend,
  onPreview,
  onDuplicate,
  onSaveAsTemplate,
  onReset,
  validation,
  autoSaveStatus,
  className = ""
}: BudgetFormActionsProps) {
  const { formState, getValues } = useFormContext<BudgetFormData>();
  const { isDirty, isValid, dirtyFields } = formState;

  const values = getValues();
  const hasItems = values.items && values.items.length > 0;
  const hasClient = !!values.client_id;
  const hasRequiredFields = hasItems && hasClient;

  // Calculate completion percentage
  const calculateCompletion = () => {
    let completed = 0;
    let total = 5; // Total fields to check

    if (hasClient) completed++;
    if (hasItems) completed++;
    if (values.title) completed++;
    if (values.payment_terms) completed++;
    if (values.validity_type && values.validity_type !== "unlimited") {
      if (values.validity_type === "days" && values.validity_days) completed++;
      else if (values.validity_type === "date" && values.validity_date) completed++;
    } else if (values.validity_type === "unlimited") {
      completed++;
    }

    return Math.round((completed / total) * 100);
  };

  const completionPercentage = calculateCompletion();

  // Get validation status
  const getValidationStatus = () => {
    if (!validation) return null;

    if (validation.isValid) {
      return {
        icon: CheckCircle2,
        color: "text-green-600",
        bgColor: "bg-green-50",
        borderColor: "border-green-200",
        message: "Orçamento válido"
      };
    }

    if (validation.errors.length > 0) {
      return {
        icon: AlertTriangle,
        color: "text-red-600",
        bgColor: "bg-red-50",
        borderColor: "border-red-200",
        message: `${validation.errors.length} erro(s) encontrado(s)`
      };
    }

    return {
      icon: Clock,
      color: "text-yellow-600",
      bgColor: "bg-yellow-50",
      borderColor: "border-yellow-200",
      message: "Atenção aos detalhes"
    };
  };

  const validationStatus = getValidationStatus();

  // Calculate totals for display
  const calculateTotals = () => {
    const items = values.items || [];
    const subtotal = items.reduce((sum, item) => sum + (item.total_value || 0), 0);
    const discountPercentage = values.discount_percentage || 0;
    const discountAmount = values.discount_amount || 0;

    let discount = discountAmount;
    if (discountPercentage > 0 && !discountAmount) {
      discount = subtotal * (discountPercentage / 100);
    }

    const finalValue = Math.max(0, subtotal - discount);

    return { subtotal, discount, finalValue };
  };

  const { subtotal, discount, finalValue } = calculateTotals();

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Status and Progress */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Completion Progress */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Progresso do Formulário</span>
                <span className="text-sm text-muted-foreground">{completionPercentage}%</span>
              </div>
              <Progress value={completionPercentage} className="h-2" />
              <div className="flex items-center gap-2 mt-2">
                {completionPercentage === 100 ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : (
                  <Clock className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-sm text-muted-foreground">
                  {completionPercentage === 100
                    ? "Formulário completo"
                    : `Preencha os campos obrigatórios para continuar`
                  }
                </span>
              </div>
            </div>

            <Separator />

            {/* Validation Status */}
            {validationStatus && (
              <div className={`flex items-center gap-3 p-3 rounded-lg border ${validationStatus.bgColor} ${validationStatus.borderColor}`}>
                <validationStatus.icon className={`h-5 w-5 ${validationStatus.color}`} />
                <div className="flex-1">
                  <div className={`font-medium ${validationStatus.color}`}>
                    {validationStatus.message}
                  </div>
                  {validation.errors && validation.errors.length > 0 && (
                    <div className="text-sm text-muted-foreground mt-1">
                      {validation.errors.slice(0, 2).join(", ")}
                      {validation.errors.length > 2 && ` e mais ${validation.errors.length - 2} erro(s)`}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Auto-save Status */}
            {autoSaveStatus && (
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  {autoSaveStatus.status === "saving" && (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary"></div>
                      <span className="text-muted-foreground">Salvando automaticamente...</span>
                    </>
                  )}
                  {autoSaveStatus.status === "saved" && autoSaveStatus.lastSaved && (
                    <>
                      <CheckCircle2 className="h-3 w-3 text-green-600" />
                      <span className="text-muted-foreground">
                        Salvo às {autoSaveStatus.lastSaved.toLocaleTimeString("pt-BR", {
                          hour: "2-digit",
                          minute: "2-digit"
                        })}
                      </span>
                    </>
                  )}
                  {autoSaveStatus.status === "error" && (
                    <>
                      <AlertTriangle className="h-3 w-3 text-red-600" />
                      <span className="text-red-600">Erro ao salvar</span>
                    </>
                  )}
                </div>
                {isDirty && (
                  <Badge variant="secondary" className="text-xs">
                    Não salvo
                  </Badge>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick Summary */}
      {hasItems && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Resumo Rápido
              </h4>
              <div className="grid gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Itens:</span>
                  <span className="font-medium">{values.items?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal:</span>
                  <span className="font-medium">
                    {new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL"
                    }).format(subtotal)}
                  </span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Desconto:</span>
                    <span className="font-medium">
                      -{new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL"
                      }).format(discount)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-base">
                  <span>Total:</span>
                  <span className="text-primary">
                    {new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL"
                    }).format(finalValue)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Primary Actions */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Main Actions */}
            <div className="grid gap-3">
              <Button
                onClick={onSave}
                disabled={!hasRequiredFields || isSubmitting}
                className="w-full"
                size="lg"
              >
                <Save className="h-4 w-4 mr-2" />
                {isSubmitting
                  ? "Salvando..."
                  : isEditing
                  ? "Salvar Alterações"
                  : "Criar Orçamento"
                }
              </Button>

              {isEditing && onSaveAndSend && (
                <Button
                  onClick={onSaveAndSend}
                  disabled={!hasRequiredFields || isSubmitting}
                  variant="default"
                  className="w-full"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {isSubmitting ? "Enviando..." : "Salvar e Enviar por E-mail"}
                </Button>
              )}
            </div>

            {/* Secondary Actions */}
            <Separator />

            <div className="grid gap-3">
              <div className="grid grid-cols-2 gap-3">
                {isEditing && onPreview && (
                  <Button
                    onClick={onPreview}
                    variant="outline"
                    disabled={!hasRequiredFields}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Visualizar
                  </Button>
                )}

                {isEditing && onDuplicate && (
                  <Button
                    onClick={onDuplicate}
                    variant="outline"
                    disabled={!hasRequiredFields}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Duplicar
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                {onSaveAsTemplate && (
                  <Button
                    onClick={onSaveAsTemplate}
                    variant="outline"
                    disabled={!hasItems}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Salvar Modelo
                  </Button>
                )}

                {onReset && (
                  <Button
                    onClick={onReset}
                    variant="outline"
                    disabled={!isDirty}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Redefinir
                  </Button>
                )}
              </div>
            </div>

            {/* Help Text */}
            <div className="text-xs text-muted-foreground text-center">
              {isEditing
                ? "As alterações serão salvas automaticamente ao clicar em salvar."
                : "Preencha todos os campos obrigatórios para criar o orçamento."
              }
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}